<div *ngIf="isOpen" class="fixed inset-0 z-[99999] flex items-center justify-center">

  <!-- Backdrop -->
  <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" (click)="onCancel()"></div>

  <!-- Video Capture Modal -->
  <div class="relative z-10 w-full max-w-lg mx-4 rounded-3xl overflow-hidden shadow-2xl bg-gray-950 flex flex-col"
    style="max-height: 90vh;">

    <!-- ── Header ──────────────────────────────────────────────── -->
    <div class="flex items-center justify-between px-5 py-4 bg-gray-950 border-b border-white/10 flex-shrink-0">
      <div class="flex items-center gap-3">
        <!-- Recording live indicator -->
        <span class="relative flex h-3 w-3" *ngIf="isRecording">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </span>
        <h3 class="text-white font-semibold text-base tracking-tight">
          <ng-container *ngIf="recordedVideoUrl">Preview Recording</ng-container>
          <ng-container *ngIf="!recordedVideoUrl && isRecording">Recording — {{ formattedDuration }}</ng-container>
          <ng-container *ngIf="!recordedVideoUrl && !isRecording">
            {{ isFrontCamera ? '📱 Front Camera' : '📷 Back Camera' }} — Video
          </ng-container>
        </h3>
      </div>
      <button (click)="onCancel()"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- ── Video / Preview area ────────────────────────────────── -->
    <div class="relative bg-black flex-shrink-0 overflow-hidden" style="aspect-ratio: 4/3;">

      <!-- Live viewfinder -->
      <video #videoEl
        class="w-full h-full object-cover transition-all duration-300"
        [class.hidden]="!!recordedVideoUrl"
        [style.transform]="isFrontCamera && !recordedVideoUrl ? 'scaleX(-1)' : 'none'"
        autoplay
        playsinline
        muted>
      </video>

      <!-- Recorded video preview -->
      <video #previewEl
        *ngIf="recordedVideoUrl"
        [src]="recordedVideoUrl"
        class="w-full h-full object-cover"
        controls
        playsinline>
      </video>

      <!-- Error overlay -->
      <div *ngIf="cameraError"
        class="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-950 px-6 text-center">
        <div class="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" class="text-red-400">
            <path d="M23 7l-7 5 7 5V7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div>
          <p class="text-white font-semibold text-sm mb-1">Camera Access Issue</p>
          <p class="text-gray-400 text-xs leading-relaxed">{{ cameraError }}</p>
        </div>
        <button (click)="requestCamera()"
          class="mt-1 px-5 py-2 rounded-full bg-[#D31148] text-white text-sm font-semibold hover:bg-[#b80e3d] transition-colors">
          Try Again
        </button>
      </div>

      <!-- Loading overlay -->
      <div *ngIf="!streamActive && !cameraError && !recordedVideoUrl"
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-950">
        <div class="w-10 h-10 rounded-full border-2 border-white/20 border-t-[#D31148] animate-spin"></div>
        <p class="text-gray-400 text-xs">
          {{ isSwitching ? 'Switching camera…' : 'Starting camera…' }}
        </p>
      </div>

      <!-- Recording timer badge (top-left) -->
      <div *ngIf="isRecording"
        class="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
        <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
        <span class="text-white text-xs font-bold font-mono">{{ formattedDuration }}</span>
      </div>

      <!-- Front/Back badge (top-right, live only) -->
      <div *ngIf="streamActive && !recordedVideoUrl"
        class="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm">
        <span class="text-white text-[10px] font-semibold tracking-wide uppercase">
          {{ isFrontCamera ? 'Front' : 'Back' }}
        </span>
      </div>
    </div>

    <!-- ── Controls ─────────────────────────────────────────────── -->
    <div class="flex-shrink-0 bg-gray-950 px-5 py-5">

      <!-- LIVE / RECORDING state -->
      <ng-container *ngIf="!recordedVideoUrl">

        <!-- Main control row: Switch · Record/Stop · Spacer -->
        <div class="flex items-center justify-between gap-2">

          <!-- Switch camera (disabled while recording) -->
          <button (click)="switchCamera()"
            [disabled]="!streamActive || isSwitching || isRecording"
            class="flex flex-col items-center gap-1 disabled:opacity-30 transition-opacity flex-shrink-0"
            title="{{ isFrontCamera ? 'Switch to back camera' : 'Switch to front camera' }}">
            <div class="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors relative overflow-hidden">
              <svg *ngIf="!isSwitching" width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-white">
                <path d="M20 7H9a3 3 0 0 0-3 3v.5M4 17h11a3 3 0 0 0 3-3v-.5"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M17 4l3 3-3 3M7 20l-3-3 3-3"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div *ngIf="isSwitching"
                class="w-5 h-5 rounded-full border-2 border-white/20 border-t-white animate-spin">
              </div>
            </div>
            <span class="text-[10px] text-gray-400 font-medium leading-tight">
              {{ isFrontCamera ? 'Back' : 'Front' }}
            </span>
          </button>

          <!-- Record / Stop button (centre, largest) -->
          <button
            [disabled]="!streamActive"
            (click)="isRecording ? stopRecording() : startRecording()"
            class="relative flex items-center justify-center rounded-full disabled:opacity-30 active:scale-95 transition-transform focus:outline-none flex-shrink-0"
            style="width: 72px; height: 72px;"
            [title]="isRecording ? 'Stop recording' : 'Start recording'">
            <span class="absolute inset-0 rounded-full border-4 transition-colors"
              [class.border-red-500]="isRecording"
              [class.border-white/60]="!isRecording"></span>
            <!-- Stop square (shown while recording) -->
            <span *ngIf="isRecording"
              class="rounded-md bg-red-500 shadow-lg transition-all"
              style="width: 28px; height: 28px;"></span>
            <!-- Record circle (shown while idle) -->
            <span *ngIf="!isRecording"
              class="rounded-full bg-red-500 hover:bg-red-400 shadow-lg transition-colors"
              style="width: 56px; height: 56px;"></span>
          </button>

          <!-- Right spacer (keeps button centred) -->
          <div class="w-11 h-11 flex-shrink-0"></div>
        </div>

        <!-- Named camera selector (desktop with 3+ cameras) -->
        <div *ngIf="availableCameras.length > 2" class="mt-4 flex gap-2 justify-center flex-wrap">
          <button *ngFor="let cam of availableCameras; let i = index"
            (click)="switchCamera()"
            [disabled]="isSwitching || isRecording"
            class="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border disabled:opacity-50"
            [class.bg-[#D31148]]="cam.deviceId === selectedCameraId"
            [class.border-[#D31148]]="cam.deviceId === selectedCameraId"
            [class.text-white]="cam.deviceId === selectedCameraId"
            [class.bg-transparent]="cam.deviceId !== selectedCameraId"
            [class.border-white/20]="cam.deviceId !== selectedCameraId"
            [class.text-gray-300]="cam.deviceId !== selectedCameraId">
            {{ cam.label || ('Camera ' + (i + 1)) }}
          </button>
        </div>

        <p class="mt-3 text-center text-[11px] leading-snug"
          [class.text-red-400]="isRecording"
          [class.text-gray-500]="!isRecording">
          {{ isRecording ? 'Tap stop when done · ' + formattedDuration : 'Tap the button to start recording with audio' }}
        </p>

      </ng-container>

      <!-- PREVIEW state -->
      <ng-container *ngIf="recordedVideoUrl">
        <div class="flex items-center gap-3">
          <button (click)="retakeVideo()"
            class="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Retake
          </button>
          <button (click)="useVideo()"
            class="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#D31148] hover:bg-[#b80e3d] text-white text-sm font-semibold transition-colors shadow-lg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Use Video
          </button>
        </div>
        <p class="mt-3 text-center text-[11px] text-gray-500 leading-snug">Duration: {{ formattedDuration }}</p>
      </ng-container>

    </div>
  </div>
</div>
